import {
  createIntranetChatMessageRequestSchema,
  createIntranetGroupChatResponseSchema,
  forwardIntranetChatResponseSchema,
  intranetChatConversationListResponseSchema,
  intranetChatMessageSchema,
  intranetChatMessagesResponseSchema,
  intranetChatPhotosResponseSchema,
  intranetChatPreferenceResponseSchema,
  intranetChatSearchResponseSchema,
  openIntranetChatResponseSchema,
  type CreateIntranetGroupChatRequest,
  type IntranetChatPreferenceRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { intranetQueryKey } from '@/products/intranet/features/intranet/use-intranet';

const okSchema = z.object({ ok: z.literal(true) });

export const intranetChatQueryKey = [...intranetQueryKey, 'chat'] as const;
export const intranetChatConversationsKey = [...intranetChatQueryKey, 'conversations'] as const;

export function intranetChatMessagesKey(conversationId: string) {
  return [...intranetChatQueryKey, 'messages', conversationId] as const;
}

const CHAT_REFETCH_MS = 15_000;

export function useIntranetChatConversations() {
  return useQuery({
    queryKey: intranetChatConversationsKey,
    queryFn: () =>
      apiFetch('/intranet/chat/conversations', intranetChatConversationListResponseSchema),
    refetchInterval: CHAT_REFETCH_MS,
  });
}

/** Polls while the thread is open so both sides see new messages without a socket yet. */
export function useIntranetChatMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: intranetChatMessagesKey(conversationId ?? ''),
    queryFn: () =>
      apiFetch(
        `/intranet/chat/conversations/${conversationId}/messages`,
        intranetChatMessagesResponseSchema,
      ),
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? CHAT_REFETCH_MS : false,
  });
}

export function useOpenIntranetChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch('/intranet/chat/open', openIntranetChatResponseSchema, {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    onSuccess: (result) => {
      if (result.mode === 'portal') {
        void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
      }
    },
  });
}

export function useCreateIntranetGroupChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetGroupChatRequest) =>
      apiFetch('/intranet/chat/groups', createIntranetGroupChatResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
    },
  });
}

export function fetchIntranetChatMessages(conversationId: string, cursor?: string) {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return apiFetch(
    `/intranet/chat/conversations/${conversationId}/messages${query}`,
    intranetChatMessagesResponseSchema,
  );
}

export function useSendIntranetChatMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      body: string;
      file?: File;
      voice?: boolean;
      replyToMessageId?: string;
      latitudeE7?: number;
      longitudeE7?: number;
    }) => {
      const hasLocation = input.latitudeE7 !== undefined && input.longitudeE7 !== undefined;
      if (input.file && hasLocation) {
        throw new Error('Ein Standort und ein Anhang gehen nicht in derselben Nachricht.');
      }
      if (input.file) {
        const form = new FormData();
        form.set('body', input.body);
        if (input.voice) {
          form.set('voice', 'true');
        }
        if (input.replyToMessageId) {
          form.set('replyToMessageId', input.replyToMessageId);
        }
        form.set('file', input.file);
        return apiFetch(
          `/intranet/chat/conversations/${conversationId}/messages`,
          intranetChatMessageSchema,
          { method: 'POST', body: form },
        );
      }
      const parsed = createIntranetChatMessageRequestSchema.parse({
        body: input.body,
        replyToMessageId: input.replyToMessageId,
        latitudeE7: input.latitudeE7,
        longitudeE7: input.longitudeE7,
      });
      if (!parsed.body.trim() && !hasLocation) {
        throw new Error('Bitte eine Nachricht eingeben.');
      }
      return apiFetch(
        `/intranet/chat/conversations/${conversationId}/messages`,
        intranetChatMessageSchema,
        { method: 'POST', body: JSON.stringify(parsed) },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intranetChatMessagesKey(conversationId) });
      void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
    },
  });
}

export function useForwardIntranetChatMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { messageId: string; targetConversationIds: string[]; comment?: string }) =>
      apiFetch(
        `/intranet/chat/conversations/${conversationId}/forward`,
        forwardIntranetChatResponseSchema,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intranetChatQueryKey });
    },
  });
}

export function useIntranetChatPhotos(conversationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...intranetChatQueryKey, 'photos', conversationId ?? ''],
    queryFn: () =>
      apiFetch(
        `/intranet/chat/conversations/${conversationId}/photos`,
        intranetChatPhotosResponseSchema,
      ),
    enabled: Boolean(conversationId) && enabled,
  });
}

export function fetchIntranetChatPhotos(conversationId: string, cursor: string) {
  return apiFetch(
    `/intranet/chat/conversations/${conversationId}/photos?cursor=${encodeURIComponent(cursor)}`,
    intranetChatPhotosResponseSchema,
  );
}

export function useMarkIntranetChatRead(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/intranet/chat/conversations/${conversationId}/read`, okSchema, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
    },
  });
}

export function useSetIntranetChatPreference(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IntranetChatPreferenceRequest) =>
      apiFetch(
        `/intranet/chat/conversations/${conversationId}/preference`,
        intranetChatPreferenceResponseSchema,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: intranetChatConversationsKey });
    },
  });
}

export function useIntranetChatSearch(term: string) {
  const q = term.trim();
  return useQuery({
    queryKey: [...intranetChatQueryKey, 'search', q],
    queryFn: () =>
      apiFetch(
        `/intranet/chat/search?q=${encodeURIComponent(q)}`,
        intranetChatSearchResponseSchema,
      ),
    enabled: q.length >= 2,
  });
}
